import type { Action } from '../../domain/action'
import { missionRace, type Objective } from '../../domain/objective'
import { MissionRaceTracks, useMissionClock } from './MissionRaceTracks'
import { missionCopy } from '../missionCopy'
import type { Area } from '../../domain/area'
import { levelFor, levelXp } from '../../domain/area'
import { AreaIcon, ChevronIcon, MoreIcon } from './Icons'
import { useI18n } from '../../i18n'

interface AreaCardProps {
  area: Area
  objective: Objective | null
  actions: Action[]
  activeAction: Action | null
  onOpen: (area: Area) => void
  onEdit: (area: Area) => void
}

export const AreaCard = ({ area, objective, actions, activeAction, onOpen, onEdit }: AreaCardProps) => {
  const { language, copy: { common, areaCard } } = useI18n()
  const now = useMissionClock()
  const currentXp = levelXp(area.xp)
  const race = objective ? missionRace(objective, actions, now) : null

  return (
    <article className="area-card">
      <button className="area-card__main" type="button" onClick={() => onOpen(area)} aria-label={`${areaCard.open} ${area.name}`} aria-describedby={`area-summary-${area.id}`}>
        <div className="area-card__header">
        <div className="area-card__icon" style={{ color: area.color, backgroundColor: `${area.color}29` }}>
          <AreaIcon />
        </div>
        <div className="area-card__identity">
          <h2>{area.name}</h2>
          <strong style={{ color: area.color }}>{common.level} {levelFor(area.xp)}</strong>
        </div>
        <ChevronIcon className="area-card__chevron" />
        </div>
        <div className="xp-track" aria-label={`${currentXp} ${areaCard.progress}`}>
          <span style={{ width: `${currentXp / 10}%`, backgroundColor: area.color }} />
        </div>
        <div className="xp-labels">
          <span>{currentXp} XP</span>
          <span>{1000 - currentXp} {areaCard.nextLevel}</span>
        </div>
        <p className="eyebrow">{areaCard.currentGoal}</p>
        <p className="area-card__objective">{objective?.name ?? areaCard.noGoal}</p>
        {objective && <MissionRaceTracks objective={objective} actions={actions} compact now={now} />}
        {activeAction && <p className="area-card__active">▶ {missionCopy[language].activeAction}: {activeAction.name}</p>}
      </button>
      <span className="visually-hidden" id={`area-summary-${area.id}`}>{common.level} {levelFor(area.xp)}. {currentXp} XP. {objective?.name ?? areaCard.noGoal}{race && `. ${missionCopy[language].you}: ${Math.round(race.progress * 100)}%.`}{race && objective?.deadline && ` ${missionCopy[language].time}: ${Math.round(race.time * 100)}%.`}</span>
      <button className="icon-button area-card__menu" type="button" onClick={() => onEdit(area)} aria-label={`${areaCard.edit} ${area.name}`}>
        <MoreIcon />
      </button>
    </article>
  )
}
